<template>
  <div
    v-clickoutside="close"
    class="company-switcher"
    @keydown.esc="closeAndFocusTrigger"
    @focusout="onFocusOut"
  >
    <company-switcher-trigger
      ref="trigger"
      :label="currentLabel"
      :panel-id="panelId"
      :open="isOpen"
      @toggle="toggle"
      @open="open"
    />

    <transition name="company-switcher-panel">
      <div v-if="isOpen" :id="panelId" class="company-switcher__panel">
        <company-switcher-search-field
          ref="search"
          v-model="term"
          :listbox-id="listboxId"
          :active-option-id="activeOptionId"
          @keydown.native="onSearchKeydown"
        />

        <company-switcher-option-list
          :listbox-id="listboxId"
          :options="filtered"
          :current-value="client"
          :active-index="activeIndex"
          @activate="activeIndex = $event"
          @select="select"
        />

        <company-switcher-create-button v-if="canCreate" @create="create" />
      </div>
    </transition>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import Clickoutside from 'element-ui/lib/utils/clickoutside'
import Company from '@/layout/mixin/Company'
import CompanyCreation from '@/layout/mixin/CompanyCreation'
import { normalizeSearchText } from '@/utils/search'
import CompanySwitcherTrigger from './Trigger'
import CompanySwitcherSearchField from './SearchField'
import CompanySwitcherOptionList from './OptionList'
import CompanySwitcherCreateButton from './CreateButton'
import { companyOptionId } from './company-option-id'

const KEY_ACTIONS = {
  ArrowDown: (vm) => vm.move(1),
  ArrowUp: (vm) => vm.move(-1),
  Enter: (vm) => vm.selectActive()
}

export default {
  name: 'CompanySwitcher',
  components: {
    CompanySwitcherTrigger,
    CompanySwitcherSearchField,
    CompanySwitcherOptionList,
    CompanySwitcherCreateButton
  },
  directives: { Clickoutside },
  mixins: [Company, CompanyCreation],
  data() {
    return {
      isOpen: false,
      term: '',
      activeIndex: -1
    }
  },
  computed: {
    ...mapGetters(['device']),
    panelId() {
      return `company-switcher-panel-${this._uid}`
    },
    listboxId() {
      return `company-switcher-listbox-${this._uid}`
    },
    currentLabel() {
      const current = this.companyOptions.find(
        (option) => option.value === this.client
      )
      return current ? current.label : ''
    },
    filtered() {
      const query = normalizeSearchText(this.term)
      if (!query) return this.companyOptions

      return this.companyOptions.filter((option) =>
        normalizeSearchText(option.label).includes(query)
      )
    },
    activeOption() {
      return this.filtered[this.activeIndex]
    },
    activeOptionId() {
      return this.activeOption
        ? companyOptionId(this.listboxId, this.activeIndex)
        : null
    }
  },
  watch: {
    filtered: 'reset'
  },
  methods: {
    reset() {
      const currentIndex = this.filtered.findIndex(
        (option) => option.value === this.client
      )

      if (currentIndex >= 0) this.activeIndex = currentIndex
      else this.activeIndex = this.filtered.length ? 0 : -1
    },
    move(step) {
      const total = this.filtered.length
      if (!total) return

      this.activeIndex = (this.activeIndex + step + total) % total
    },
    async open() {
      if (this.isOpen) return

      this.term = ''
      this.reset()
      this.isOpen = true

      if (this.device === 'mobile') return
      await this.$nextTick()
      if (this.$refs.search) this.$refs.search.focus()
    },
    close() {
      this.isOpen = false
    },
    toggle() {
      if (this.isOpen) this.close()
      else this.open()
    },
    focusTrigger() {
      this.$refs.trigger.$el.focus()
    },
    closeAndFocusTrigger() {
      this.close()
      this.focusTrigger()
    },
    select(code) {
      this.close()
      this.selectCompany(code)
    },
    create() {
      this.close()
      this.goToCreate()
    },
    selectActive() {
      if (!this.activeOption) return

      this.focusTrigger()
      this.select(this.activeOption.value)
    },
    onSearchKeydown(event) {
      const action = KEY_ACTIONS[event.key]
      if (!action) return

      event.preventDefault()
      action(this)
    },
    onFocusOut(event) {
      const next = event.relatedTarget
      if (next && !this.$el.contains(next)) this.close()
    }
  }
}
</script>
